import type { CreateKind } from './types';

export interface SelectOption {
  value: string;
  label: string;
  /** false = renders in the dropdown but disabled, with a "coming soon"
   * hint — no golden template exists for it yet. Never silently drop an
   * option the approved spec lists; be honest about what's real instead. */
  enabled: boolean;
}

export interface AwsNeedOption {
  key: 'S3' | 'SQS' | 'DynamoDB' | 'Database' | 'Secrets' | 'Queue';
  label: string;
}

export interface CreateFlowFieldConfig {
  nameLabel: string;
  namePlaceholder: string;
  runtimeLabel: string;
  runtimeOptions: SelectOption[];
  /** Maps a selected runtime option value -> manifest `application.runtime`. */
  runtimeToManifestRuntime: Record<string, 'node' | 'python' | 'static'>;
  /** Maps a selected runtime option value -> the golden template that backs
   * it (see TEMPLATE_DIRS in platform-controller/src/applications/applications.service.ts).
   * Falls back to CREATE_KIND_CONFIG[kind].template when a kind has only one
   * real template and hasn't been given a per-runtime map. */
  runtimeToTemplate?: Record<string, string>;
  hasModelProvider: boolean;
  modelProviderOptions?: SelectOption[];
  awsNeeds: AwsNeedOption[];
  hasSchedule: boolean;
  environmentOptions: SelectOption[];
  defaultPort: number;
}

export const CREATE_FLOW_FIELDS: Record<Exclude<CreateKind, 'compose'>, CreateFlowFieldConfig> = {
  frontend: {
    nameLabel: 'Application Name',
    namePlaceholder: 'my-storefront',
    runtimeLabel: 'Framework',
    runtimeOptions: [
      { value: 'nextjs', label: 'Next.js', enabled: true },
      { value: 'react-vite', label: 'React/Vite', enabled: true },
      { value: 'vue', label: 'Vue', enabled: true },
    ],
    runtimeToManifestRuntime: { nextjs: 'static', 'react-vite': 'static', vue: 'static' },
    runtimeToTemplate: {
      nextjs: 'frontend-nextjs',
      'react-vite': 'frontend-react-vite',
      vue: 'frontend-vue',
    },
    hasModelProvider: false,
    awsNeeds: [],
    hasSchedule: false,
    environmentOptions: [
      { value: 'development', label: 'Development', enabled: true },
      { value: 'staging', label: 'Staging', enabled: true },
    ],
    defaultPort: 3000,
  },
  backend: {
    nameLabel: 'Service Name',
    namePlaceholder: 'catalog-api',
    runtimeLabel: 'Runtime',
    runtimeOptions: [
      { value: 'nestjs', label: 'NestJS', enabled: true },
      { value: 'fastapi', label: 'FastAPI', enabled: true },
    ],
    runtimeToManifestRuntime: { nestjs: 'node', fastapi: 'python' },
    runtimeToTemplate: { nestjs: 'backend-node-nestjs', fastapi: 'backend-python-fastapi' },
    hasModelProvider: false,
    awsNeeds: [
      { key: 'S3', label: 'S3' },
      { key: 'SQS', label: 'SQS' },
      { key: 'DynamoDB', label: 'DynamoDB' },
      { key: 'Database', label: 'Database' },
      { key: 'Secrets', label: 'Secrets' },
    ],
    hasSchedule: false,
    environmentOptions: [
      { value: 'development', label: 'Development', enabled: true },
      { value: 'staging', label: 'Staging', enabled: true },
    ],
    defaultPort: 3000,
  },
  'ai-agent': {
    nameLabel: 'Agent Name',
    namePlaceholder: 'invoice-analyzer',
    runtimeLabel: 'Runtime',
    runtimeOptions: [
      { value: 'python', label: 'Python', enabled: true },
      { value: 'nodejs', label: 'Node.js', enabled: false },
    ],
    runtimeToManifestRuntime: { python: 'python', nodejs: 'node' },
    hasModelProvider: true,
    modelProviderOptions: [
      { value: 'bedrock', label: 'Bedrock', enabled: true },
      { value: 'openai-compatible', label: 'OpenAI-compatible', enabled: true },
      { value: 'internal-model', label: 'Internal Model', enabled: true },
    ],
    awsNeeds: [
      { key: 'S3', label: 'S3' },
      { key: 'SQS', label: 'SQS' },
      { key: 'DynamoDB', label: 'DynamoDB' },
      { key: 'Database', label: 'Database' },
    ],
    hasSchedule: false,
    environmentOptions: [
      { value: 'development', label: 'Development', enabled: true },
      { value: 'staging', label: 'Staging', enabled: true },
    ],
    defaultPort: 8000,
  },
  worker: {
    nameLabel: 'Worker Name',
    namePlaceholder: 'email-worker',
    runtimeLabel: 'Runtime',
    runtimeOptions: [
      { value: 'nodejs', label: 'Node.js', enabled: true },
      { value: 'python', label: 'Python', enabled: false },
    ],
    runtimeToManifestRuntime: { nodejs: 'node', python: 'python' },
    hasModelProvider: false,
    awsNeeds: [
      { key: 'Queue', label: 'Needs Queue?' },
      { key: 'Database', label: 'Needs Database?' },
    ],
    hasSchedule: false,
    environmentOptions: [
      { value: 'development', label: 'Development', enabled: true },
      { value: 'staging', label: 'Staging', enabled: true },
    ],
    defaultPort: 3000,
  },
  'scheduled-job': {
    nameLabel: 'Job Name',
    namePlaceholder: 'nightly-report',
    runtimeLabel: 'Runtime',
    runtimeOptions: [
      { value: 'nodejs', label: 'Node.js', enabled: true },
      { value: 'python', label: 'Python', enabled: false },
    ],
    runtimeToManifestRuntime: { nodejs: 'node', python: 'python' },
    hasModelProvider: false,
    awsNeeds: [],
    hasSchedule: true,
    environmentOptions: [
      { value: 'development', label: 'Development', enabled: true },
      { value: 'staging', label: 'Staging', enabled: true },
    ],
    defaultPort: 3000,
  },
  fullstack: {
    nameLabel: 'Application Name',
    namePlaceholder: 'my-app',
    runtimeLabel: 'Stack',
    runtimeOptions: [{ value: 'react-nestjs', label: 'React (Vite) + NestJS', enabled: true }],
    runtimeToManifestRuntime: { 'react-nestjs': 'node' },
    hasModelProvider: false,
    // Needs apply to the backend half — the frontend is a static bundle.
    awsNeeds: [
      { key: 'S3', label: 'S3' },
      { key: 'SQS', label: 'SQS' },
      { key: 'DynamoDB', label: 'DynamoDB' },
      { key: 'Database', label: 'Database' },
      { key: 'Secrets', label: 'Secrets' },
    ],
    hasSchedule: false,
    environmentOptions: [
      { value: 'development', label: 'Development', enabled: true },
      { value: 'staging', label: 'Staging', enabled: true },
    ],
    defaultPort: 3000,
  },
};

/** Granular workflow-reported statuses collapsed into the 5 simple stages
 * the approved UI shows — real backend state, not a timer. */
export const PROGRESS_STAGES = [
  'Creating repository',
  'Preparing application',
  'Provisioning resources',
  'Deploying',
  'Health check',
] as const;

/** Same 5 stages, worded for DeployExistingRepositoryWorkflow — it clones an
 * existing repo (read-only) rather than creating one, and a user picking
 * "Existing GitHub repo" should never see a label implying a new repo was
 * made. See CLONING_REPOSITORY below. */
export const PROGRESS_STAGES_EXISTING_REPO = [
  'Cloning repository',
  'Preparing application',
  'Provisioning resources',
  'Deploying',
  'Health check',
] as const;

const STAGE_BY_STATUS: Record<string, number> = {
  STARTED: 0,
  VALIDATING_SCHEMA: 0,
  VALIDATING_POLICY: 0,
  CREATING_REPOSITORY: 0,
  PUSHING_TEMPLATE: 0,
  // DeployExistingRepositoryWorkflow — clones instead of creating/pushing.
  CLONING_REPOSITORY: 0,
  DETERMINING_EXECUTION_TARGET: 1,
  GENERATING_TFVARS: 1,
  TOFU_INIT: 1,
  TOFU_PLAN: 1,
  BUILDING_IMAGE: 1,
  TOFU_APPLY: 2,
  VERIFYING_RESOURCES: 2,
  PREPARING_COOLIFY: 2,
  CREATING_APPLICATION: 2,
  TAGGING_APPLICATION: 2,
  DEPLOYING: 3,
  WAITING_FOR_DEPLOYMENT: 3,
  VERIFYING_HEALTH: 4,
  // Waits for the public https://*.coolify.mumzstage.com URL to have a real,
  // issued TLS cert before handing it back — see verifyPublicHttpsActivity's
  // doc comment in coolify-activities.ts for the live bug (a browser SSL
  // warning) this closes.
  VERIFYING_PUBLIC_ACCESS: 4,
  PERSISTING_EVIDENCE: 4,

  // CreateFullStackApplicationWorkflow — backend half first, then frontend.
  VALIDATING_BACKEND_SCHEMA: 0,
  CREATING_BACKEND_REPOSITORY: 0,
  PUSHING_BACKEND_TEMPLATE: 0,
  BUILDING_BACKEND_IMAGE: 1,
  CREATING_BACKEND_APPLICATION: 2,
  TAGGING_BACKEND_APPLICATION: 2,
  DEPLOYING_BACKEND: 3,
  WAITING_FOR_BACKEND_DEPLOYMENT: 3,
  VERIFYING_BACKEND_HEALTH: 4,
  VERIFYING_BACKEND_PUBLIC_ACCESS: 4,
  VALIDATING_FRONTEND_SCHEMA: 0,
  CREATING_FRONTEND_REPOSITORY: 0,
  PUSHING_FRONTEND_TEMPLATE: 0,
  BUILDING_FRONTEND_IMAGE: 1,
  CREATING_FRONTEND_APPLICATION: 2,
  TAGGING_FRONTEND_APPLICATION: 2,
  DEPLOYING_FRONTEND: 3,
  WAITING_FOR_FRONTEND_DEPLOYMENT: 3,
  VERIFYING_FRONTEND_HEALTH: 4,
  VERIFYING_FRONTEND_PUBLIC_ACCESS: 4,
};

export function stageIndexForStatus(status: string): number {
  return STAGE_BY_STATUS[status] ?? 0;
}
